import { useEffect, useRef, useState } from "react";
import { useAuthContext } from "../../context/auth/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useUserContext } from "../../context/user_context/UserContext";
import { useOrganizationContext } from "../../context/organization_context/OrganizationContext";
import { useSubscriptionContext } from "../../context/subscription_context/SubscriptionContext";
import { UserStatus } from "./routing_statuses/RoutingStatuses";
import LoadingNavBar from "../navbar/LoadingNavBar";
import { Rings } from "react-loader-spinner";
import { Container } from "react-bootstrap";
import { Loading } from "../loading/Loading";
import { Typography } from "@mui/material";

export const InitializationRoute = ({ children }) => {
  const { user } = useAuthContext();
  const { firebaseUser } = useUserContext();
  const { 
    organization, 
    subscriptionInitialized,
    markSubscriptionInitialized 
  } = useOrganizationContext();
  const { 
    subscription, 
    subscriptionStatus, 
    hasActiveSubscription,
    isSubscriptionLoading
  } = useSubscriptionContext();
  const location = useLocation();
  const navigate = useNavigate();
  const initialized = useRef(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // On first mount, set a delay to avoid premature redirects
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoadComplete(true);
    }, 2000); // 2 second delay
    
    return () => clearTimeout(timer);
  }, []);
  
  // Determine if subscription data is ready for evaluation
  const isSubscriptionReady = !isSubscriptionLoading && 
                            subscription !== UserStatus.UNINITIALIZED;

  // Check if subscription is initialized via either context or Firebase flag
  const isSubscriptionCompleted = subscriptionInitialized || organization?.subscription_initialized === true;

  // Detect if basic profile is incomplete - we check this first before any other checks
  const hasIncompleteProfile = user !== UserStatus.UNAUTHENTICATED && 
                               user !== UserStatus.UNINITIALIZED && 
                               (
                                 // BASIC PROFILE CHECKS - Always required regardless of subscription state
                                 // Check specifically for a missing profile (not just initializing)
                                 (firebaseUser === UserStatus.DOES_NOT_EXIST) ||
                                 // Or firebaseUser exists but has no real data (new signup)
                                 (firebaseUser && !firebaseUser.first_name) ||
                                 // Or truly missing organization 
                                 (organization === UserStatus.DOES_NOT_EXIST) ||
                                 // Or organization exists but is empty (new signup)
                                 (organization && !organization.name) ||
                                 
                                 // SUBSCRIPTION CHECKS - Only check these if subscription is not completed
                                 (!isSubscriptionCompleted && (
                                   // Missing subscription
                                   (subscription === UserStatus.DOES_NOT_EXIST) ||
                                   // Or subscription doesn't have customer data
                                   (subscription && !subscription.customer) ||
                                   // Or subscription is not active/trialing (needs payment info)
                                   (!hasActiveSubscription && isSubscriptionReady)
                                 ))
                               );
                               
  // For debugging, identify exactly what condition triggered hasIncompleteProfile                          
  const incompleteProfileReason = user !== UserStatus.UNAUTHENTICATED && 
                                  user !== UserStatus.UNINITIALIZED 
    ? {
        // Basic profile issues
        missingFirebaseUser: firebaseUser === UserStatus.DOES_NOT_EXIST,
        missingFirstName: firebaseUser && !firebaseUser.first_name,
        missingOrganization: organization === UserStatus.DOES_NOT_EXIST,
        missingOrgName: organization && !organization.name,
        
        // Subscription issues - Only relevant if not initialized
        missingSubscription: !isSubscriptionCompleted && (subscription === UserStatus.DOES_NOT_EXIST),
        missingCustomer: !isSubscriptionCompleted && (subscription && !subscription.customer),
        inactiveSubscription: !isSubscriptionCompleted && (!hasActiveSubscription && isSubscriptionReady),
        
        // Initialization state
        isSubscriptionCompleted
      }
    : "Not authenticated";

  // Single initialization effect that runs once
  useEffect(() => {
    // Skip if already initialized 
    if (initialized.current) {
      return;
    }
    
    // Skip if initial load delay isn't complete yet
    if (!initialLoadComplete) {
      return;
    }
    
    // Skip if user is still loading
    if (user === UserStatus.UNINITIALIZED) {
      return;
    }

    // Skip if critical data is still initializing but not yet determined to be missing
    const isStillInitializing = 
      firebaseUser === UserStatus.UNINITIALIZED || 
      organization === UserStatus.UNINITIALIZED ||
      subscription === UserStatus.UNINITIALIZED;
      
    // Special case: If Firebase user is explicitly marked as does_not_exist,
    // this is a newly created account that needs to complete signup
    if (firebaseUser === UserStatus.DOES_NOT_EXIST) {
      // For signup-completion page, don't redirect if already there
      if (location.pathname === '/signup-completion') {
        initialized.current = true;
        setIsInitializing(false);
        return;
      }
      
      // Redirect to signup completion for new users
      navigate('/signup-completion', { 
        replace: true, 
        state: { returnUrl: location.pathname + location.search } 
      });
      initialized.current = true;
      setIsInitializing(false);
      return;
    }
      
    if (isStillInitializing) {
      return;
    }

    // For authenticated users, detect if they need to complete signup FIRST (highest priority check)
    if (hasIncompleteProfile) {
      // For signup-completion page, don't redirect if already there
      if (location.pathname === '/signup-completion') {
        initialized.current = true;
        setIsInitializing(false);
        return;
      }
      
      // Redirect to signup completion for incomplete profiles
      navigate('/signup-completion', { 
        replace: true, 
        state: { returnUrl: location.pathname + location.search } 
      });
      initialized.current = true;
      setIsInitializing(false);
      return;
    }
    
    // Handle unauthenticated users
    if (user === UserStatus.UNAUTHENTICATED) {
      navigate(`/`, { replace: true });
      initialized.current = true;
      setIsInitializing(false);
      return;
    }

    // CRITICAL FIX: The subscription data is either ready or not needed for the current flow
    // In either case, complete the initialization to avoid getting stuck
    initialized.current = true;
    setIsInitializing(false);
  }, [
    user, 
    firebaseUser, 
    organization, 
    subscription, 
    subscriptionStatus,
    hasActiveSubscription, 
    isSubscriptionReady,
    isSubscriptionLoading,
    hasIncompleteProfile,
    navigate, 
    location,
    initialLoadComplete,
    isSubscriptionCompleted
  ]);

  // Check if subscription is initialized in Firebase but not in context
  useEffect(() => {
    if (organization?.subscription_initialized === true && !subscriptionInitialized) {
      markSubscriptionInitialized();
    }
  }, [organization, subscriptionInitialized, markSubscriptionInitialized]);
  
  // Ensure initialization completes when subscription is properly initialized
  useEffect(() => {
    // Only run after initial load delay is complete
    if (!initialLoadComplete) {
      return;
    }
    
    // If all critical data is ready and subscription is initialized one way or another,
    // immediately complete initialization to avoid getting stuck
    if (user && user !== UserStatus.UNINITIALIZED && 
        user !== UserStatus.UNAUTHENTICATED && 
        firebaseUser && firebaseUser !== UserStatus.UNINITIALIZED && 
        firebaseUser !== UserStatus.DOES_NOT_EXIST &&
        organization && organization !== UserStatus.UNINITIALIZED && 
        organization !== UserStatus.DOES_NOT_EXIST &&
        isSubscriptionCompleted) {
      
      initialized.current = true;
      setIsInitializing(false);
    }
  }, [user, firebaseUser, organization, isSubscriptionCompleted, initialLoadComplete]);

  // Show loading spinner during initialization
  if (isInitializing) {
    return (
      <>
        <LoadingNavBar />
        <Loading />
      </>
    );
  }

  // If we reach here, we're initialized and can show children
  return <>{children}</>;
};
